/**
 * Chart Helper Functions
 * 
 * Utility functions for formatting and manipulating chart data.
 */

// --- Internal date helpers (UTC-safe for YYYY-MM-DD) ---
const parseISODateUTC = (iso) => {
  const s = String(iso || "");
  // If backend ever sends a full ISO timestamp, let Date parse it.
  if (s.includes("T")) return new Date(s);
  const [y, m, d] = s.split("-").map((v) => Number(v));
  if (!y || !m || !d) return new Date(s);
  return new Date(Date.UTC(y, m - 1, d));
};

const toISODateUTC = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

const getMondayUTC = (date) => {
  // Monday-based week (UTC), consistent with aggregateByWeek.
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayOfWeek = d.getUTCDay(); // 0 (Sun) - 6 (Sat)
  const diff = d.getUTCDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  d.setUTCDate(diff);
  return d;
};

const isoWeekToMondayUTC = (year, week) => {
  // ISO week 1 is the week with Jan 4th in it.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const week1Monday = getMondayUTC(jan4);
  const d = new Date(Date.UTC(week1Monday.getUTCFullYear(), week1Monday.getUTCMonth(), week1Monday.getUTCDate()));
  d.setUTCDate(d.getUTCDate() + (week - 1) * 7);
  return d;
};

const normalizePeriodKey = (period, granularity = "day") => {
  const s = String(period || "").trim();
  if (!s) return "";

  // Common backend formats we support:
  // - day:   "YYYY-MM-DD" or full ISO
  // - month: "YYYY-MM" or "YYYY-MM-01" or any day within month
  // - week:  "YYYY-Www" (ISO week), or any day within week
  if (granularity === "month") {
    const m = s.match(/^(\d{4})-(\d{2})$/);
    if (m) return `${m[1]}-${m[2]}-01`;
    const d = parseISODateUTC(s);
    if (isNaN(d.getTime())) return s;
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
  }

  if (granularity === "week") {
    const m = s.match(/^(\d{4})-W(\d{2})$/i);
    if (m) {
      const year = Number(m[1]);
      const week = Number(m[2]);
      if (Number.isFinite(year) && Number.isFinite(week) && week >= 1 && week <= 53) {
        return toISODateUTC(isoWeekToMondayUTC(year, week));
      }
    }
    const d = parseISODateUTC(s);
    if (isNaN(d.getTime())) return s;
    return toISODateUTC(getMondayUTC(d));
  }

  // day
  if (s.includes("T")) return toISODateUTC(parseISODateUTC(s));
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = parseISODateUTC(s);
  return isNaN(d.getTime()) ? s : toISODateUTC(d);
};

/**
 * Format date string for display based on granularity
 * @param {string} dateString - ISO date string (YYYY-MM-DD)
 * @param {string} granularity - 'day', 'week', or 'month'
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, granularity = 'day') => {
  if (!dateString) return '';
  
  const date = parseISODateUTC(dateString);
  
  if (isNaN(date.getTime())) {
    return dateString; // Return original if invalid
  }
  
  switch (granularity) {
    case 'month':
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    case 'week':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'day':
    default:
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

/**
 * Fill missing periods in period-based chart data with zero counts.
 * This keeps X-axis spacing faithful to elapsed time while still using a categorical axis (Recharts BarChart).
 *
 * Input shape: [{ period: 'YYYY-MM-DD', count: number }, ...]
 *
 * @param {Array} data
 * @param {'day'|'week'|'month'} granularity
 * @param {string|null} startDate - YYYY-MM-DD (optional)
 * @param {string|null} endDate - YYYY-MM-DD (optional)
 * @returns {Array} dense data sorted by period
 */
export const fillMissingPeriods = (data, granularity = "day", startDate = null, endDate = null) => {
  if (!Array.isArray(data) || data.length === 0) return [];

  const startFromData = () => {
    const sorted = [...data]
      .map((d) => ({ ...d, _t: parseISODateUTC(d?.period).getTime() }))
      .filter((d) => !isNaN(d._t))
      .sort((a, b) => a._t - b._t);
    return sorted[0]?.period || null;
  };

  const endFromData = () => {
    const sorted = [...data]
      .map((d) => ({ ...d, _t: parseISODateUTC(d?.period).getTime() }))
      .filter((d) => !isNaN(d._t))
      .sort((a, b) => a._t - b._t);
    return sorted[sorted.length - 1]?.period || null;
  };

  const startISO = startDate || startFromData();
  const endISO = endDate || endFromData();
  const start = parseISODateUTC(startISO);
  const end = parseISODateUTC(endISO);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return data;

  // Build a map of counts keyed by normalized period string.
  const countMap = new Map();
  for (const entry of data) {
    const p = normalizePeriodKey(entry?.period, granularity);
    if (!p) continue;
    const existing = countMap.get(p) || 0;
    const next = existing + Number(entry?.count || 0);
    countMap.set(p, Number.isFinite(next) ? next : existing);
  }

  const result = [];

  if (granularity === "month") {
    const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
    const endMonth = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
    while (cursor <= endMonth) {
      const period = toISODateUTC(cursor);
      result.push({ period, count: countMap.get(period) || 0 });
      cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }
    return result;
  }

  if (granularity === "week") {
    const cursor = getMondayUTC(start);
    const endWeek = getMondayUTC(end);
    while (cursor <= endWeek) {
      const period = toISODateUTC(cursor);
      result.push({ period, count: countMap.get(period) || 0 });
      cursor.setUTCDate(cursor.getUTCDate() + 7);
    }
    return result;
  }

  // Default: day
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  const endDay = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
  while (cursor <= endDay) {
    const period = toISODateUTC(cursor);
    result.push({ period, count: countMap.get(period) || 0 });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return result;
};

/**
 * Fill missing periods for stacked / multi-series period rows.
 *
 * Input shape: [{ period: '...', seriesA: number, seriesB: number, ... }, ...]
 * Output uses canonical keys:
 * - day:   YYYY-MM-DD
 * - week:  Monday YYYY-MM-DD
 * - month: YYYY-MM-01
 */
export const fillMissingPeriodRows = (
  rows,
  granularity = "day",
  startDate = null,
  endDate = null,
  { seriesKeys = null } = {}
) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const keys =
    Array.isArray(seriesKeys) && seriesKeys.length
      ? seriesKeys
      : Array.from(
          rows.reduce((acc, r) => {
            Object.keys(r || {}).forEach((k) => {
              if (k !== "period") acc.add(k);
            });
            return acc;
          }, new Set())
        );

  const normalized = rows
    .map((r) => {
      const period = normalizePeriodKey(r?.period, granularity);
      const t = parseISODateUTC(period).getTime();
      return { raw: r, period, _t: t };
    })
    .filter((r) => r.period && !isNaN(r._t))
    .sort((a, b) => a._t - b._t);

  if (normalized.length === 0) return [];

  const startISO = startDate ? normalizePeriodKey(startDate, granularity) : normalized[0].period;
  const endISO = endDate ? normalizePeriodKey(endDate, granularity) : normalized[normalized.length - 1].period;
  const start = parseISODateUTC(startISO);
  const end = parseISODateUTC(endISO);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return rows;

  // Aggregate rows into canonical period buckets (summing duplicate periods).
  const bucketMap = new Map(); // period -> {period, ...keys}
  for (const r of normalized) {
    const existing = bucketMap.get(r.period) || { period: r.period };
    for (const k of keys) {
      const prev = Number(existing[k] || 0);
      const next = prev + Number(r.raw?.[k] || 0);
      existing[k] = Number.isFinite(next) ? next : prev;
    }
    bucketMap.set(r.period, existing);
  }

  const result = [];

  if (granularity === "month") {
    const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
    const endMonth = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
    while (cursor <= endMonth) {
      const period = toISODateUTC(cursor);
      const row = bucketMap.get(period) || { period };
      keys.forEach((k) => {
        if (row[k] === undefined) row[k] = 0;
      });
      result.push(row);
      cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }
    return result;
  }

  if (granularity === "week") {
    const cursor = getMondayUTC(start);
    const endWeek = getMondayUTC(end);
    while (cursor <= endWeek) {
      const period = toISODateUTC(cursor);
      const row = bucketMap.get(period) || { period };
      keys.forEach((k) => {
        if (row[k] === undefined) row[k] = 0;
      });
      result.push(row);
      cursor.setUTCDate(cursor.getUTCDate() + 7);
    }
    return result;
  }

  // day
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  const endDay = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
  while (cursor <= endDay) {
    const period = toISODateUTC(cursor);
    const row = bucketMap.get(period) || { period };
    keys.forEach((k) => {
      if (row[k] === undefined) row[k] = 0;
    });
    result.push(row);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return result;
};

/**
 * Calculate date range based on preset
 * @param {string} preset - 'last7days', 'last30days', 'last90days', or 'alltime'
 * @returns {{ startDate: string, endDate: string }} ISO date strings (YYYY-MM-DD)
 */
export const calculateDateRange = (preset) => {
  const today = new Date();
  const endDate = today.toISOString().split('T')[0];
  
  let startDate;
  
  switch (preset) {
    case 'last7days':
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      startDate = sevenDaysAgo.toISOString().split('T')[0];
      break;
    case 'last30days':
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      startDate = thirtyDaysAgo.toISOString().split('T')[0];
      break;
    case 'last90days':
      const ninetyDaysAgo = new Date(today);
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      startDate = ninetyDaysAgo.toISOString().split('T')[0];
      break;
    case 'alltime':
      // Start from a very early date (e.g., 2020-01-01)
      startDate = '2020-01-01';
      break;
    default:
      // Default to last 30 days
      const defaultStart = new Date(today);
      defaultStart.setDate(defaultStart.getDate() - 30);
      startDate = defaultStart.toISOString().split('T')[0];
  }
  
  return { startDate, endDate };
};

/**
 * Fill in missing dates in timeseries data with zero values
 * @param {Array} data - Array of { date, value } objects
 * @param {string} startDate - ISO date string (YYYY-MM-DD)
 * @param {string} endDate - ISO date string (YYYY-MM-DD)
 * @returns {Array} Data with all dates filled in
 */
export const fillMissingDates = (data, startDate, endDate) => {
  if (!data || data.length === 0) {
    return [];
  }
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  const result = [];
  
  // Create a map of existing data points
  const dataMap = new Map(data.map(d => [d.date, d]));
  
  // Iterate through each day in the range
  const current = new Date(start);
  while (current <= end) {
    const dateString = current.toISOString().split('T')[0];
    
    if (dataMap.has(dateString)) {
      result.push(dataMap.get(dateString));
    } else {
      // Add missing date with zero values
      result.push({
        date: dateString,
        users: 0,
        containers: 0,
      });
    }
    
    current.setDate(current.getDate() + 1);
  }
  
  return result;
};

/**
 * Aggregate daily data into weekly data
 * @param {Array} dailyData - Array of { period, count } objects with daily granularity
 * @returns {Array} Weekly aggregated data
 */
export const aggregateByWeek = (dailyData) => {
  if (!dailyData || dailyData.length === 0) {
    return [];
  }
  
  const weekMap = new Map();
  
  dailyData.forEach(entry => {
    const date = new Date(entry.period);
    
    // Get the Monday of the week
    const dayOfWeek = date.getDay();
    const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust when day is Sunday
    const monday = new Date(date.setDate(diff));
    const weekKey = monday.toISOString().split('T')[0];
    
    if (weekMap.has(weekKey)) {
      weekMap.set(weekKey, weekMap.get(weekKey) + entry.count);
    } else {
      weekMap.set(weekKey, entry.count);
    }
  });
  
  // Convert map to array and sort
  return Array.from(weekMap.entries())
    .map(([period, count]) => ({ period, count }))
    .sort((a, b) => new Date(a.period) - new Date(b.period));
};

/**
 * Aggregate daily data into monthly data
 * @param {Array} dailyData - Array of { period, count } objects with daily granularity
 * @returns {Array} Monthly aggregated data
 */
export const aggregateByMonth = (dailyData) => {
  if (!dailyData || dailyData.length === 0) {
    return [];
  }
  
  const monthMap = new Map();
  
  dailyData.forEach(entry => {
    const date = new Date(entry.period);
    
    // Get the first day of the month
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
    
    if (monthMap.has(monthKey)) {
      monthMap.set(monthKey, monthMap.get(monthKey) + entry.count);
    } else {
      monthMap.set(monthKey, entry.count);
    }
  });
  
  // Convert map to array and sort
  return Array.from(monthMap.entries())
    .map(([period, count]) => ({ period, count }))
    .sort((a, b) => new Date(a.period) - new Date(b.period));
};

/**
 * Format large numbers with commas
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return num.toLocaleString('en-US');
};

/**
 * Format axis tick for large numbers (e.g., 1000 -> 1K)
 * @param {number} value - Number to format
 * @returns {string} Formatted string
 */
export const formatAxisTick = (value) => {
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  }
  if (value >= 1000) {
    return (value / 1000).toFixed(1) + 'K';
  }
  return value.toString();
};

